// WHICH STAGE creates the actual policy / loan / managed account.
//
// Dependency-free on purpose: both the API (which acts on it) and the web
// bundle (which has to TELL people when their policy will appear) import
// this. Keeping it out of shared/db/product-materialization.ts — which
// pulls in the D1 client, entity versions and the people modules — is what
// makes it safe for the browser. Same precedent as
// shared/service-records/people-labels.ts.

import {
  lookupStageTemplate,
  PLANNING_POLICY_IN_FORCE_STAGE,
} from "../db/company-stage-templates";

// ─── The trigger stage per board ───
//
// Keys and resolution order mirror the stage-template registry
// (company:transaction_type, falling back to the company's `*` default),
// so this table is read the same way the boards themselves are.
//
// An EXPLICIT null means "this board has no such stage — wait for
// completion", and must beat the company default, so callers test
// `key in TABLE` rather than truthiness.
//
// Every value below is a real slug on that board (shared/db/
// company-stage-templates.ts), asserted by
// tests/unit/product-materialization.test.ts — a typo here would silently
// mean "never materialize until completion", which is the failure mode
// this whole module exists to end.
const PRODUCT_STAGE_BY_BOARD: Record<string, string | null> = {
  // Insurance — the carrier has issued the policy. Stage 2 of 3, so the
  // policy record exists during Document Review, which is where Mylah is
  // keying in the policy number and dates.
  "insurance:*": "policy_issued",
  "insurance:policy_issuance": "policy_issued",

  // Mortgage Traditional — Grant, 2026-08-17: "When Mortgage record is in
  // processing it is a loan application. Not an actual closed loan. A loan
  // application in processing can get won/closed or lost/denied or
  // withdrawn or whatever. So, creating the actual loan record at stage
  // funded is likely the best option."
  //
  // This supersedes the 2026-08-12 decision to create the loan at
  // win/create (shared/db/mortgage-loan-create.ts). That decision was
  // solving "no creation path anywhere attached a loan" — all 17 open
  // mortgage Processing records had none, blocking Brian Baker's
  // commission worksheet. Funded still solves it: `funded` (sort 6)
  // precedes `closed_compliance` (sort 7), and 11 of those 17 sat at
  // Closed - Compliance. The loan exists by the time the worksheet is
  // wanted; it just no longer exists while the file is still an
  // application that could be denied or withdrawn.
  "mortgage:*": "funded",
  "mortgage:mortgage_loan_progression": "funded",

  // Private Money — **Closed - Compliance**, the third and last column of
  // Mike Zins's board (company-stage-templates.ts, approved 2026-09-17).
  //
  // This replaces an explicit null, which was the honest answer while PM
  // Processing was a single "Processing" column with no stage that meant
  // "the money moved": the loan could only appear when the record was
  // marked won, i.e. at the very end.
  //
  // Reading Mike's three columns against the doctrine's own test — the
  // product record is created when the product exists (CLAUDE.md, Grant
  // 2026-08-17) — Closed - Compliance is the first stage that is only
  // ever true AFTER a loan has funded. A file still in Conditions is an
  // application that can be denied or withdrawn; a file that dies goes to
  // the lost/fell-through outcome and never reaches this column at all.
  //
  // It also delivers what the plan asked for in §5.2 item 2: compliance
  // close-out runs on the Processing record "with the loan already in
  // place". Entering the column creates the loan; the close-out work then
  // happens inside it. The plan's own wording says "created at `funded`,
  // not at Closed compliance", written when everyone still assumed a
  // `funded` column — Mike's list has none, and inventing one he did not
  // ask for would be worse than reading his board honestly. Flagged for
  // him in the PR rather than decided silently.
  "mortgage:private_money_loan": "closed_compliance",
  "mortgage:private_money_loan_progression": "closed_compliance",

  // Financial — the managed portfolio exists once the assets land.
  // Everything before it (Onboarding, Waiting on Assets) is paperwork
  // against an account holding nothing.
  "financial:*": "assets_received",
  "financial:financial_client_onboarding": "assets_received",

  // Planning health / Medicare — **Policy In-Force**.
  //
  // RE-POINTED 2026-08-25 (program session S10) from `policy_received`,
  // in the same PR that re-cut this board to Ryan's five stages
  // (company-stage-templates.ts). Not a rename: `policy_received` is not
  // a column on this board any more, and a trigger naming a column that
  // no longer exists degrades silently to "nothing until completion" —
  // the exact failure this module was written to end.
  //
  // In-force is also the RIGHT stage on the doctrine's own test, not just
  // the nearest survivor. "The product record is created when the product
  // exists" (CLAUDE.md, Grant 2026-08-17) — and a policy in force IS the
  // coverage existing. The stage before it, App Approved / Payment
  // Received, is still an application the carrier could yet decline or
  // the client could yet walk away from, which is precisely the
  // "prospect until it's real" window Grant described.
  "planning:life_health_case": PLANNING_POLICY_IN_FORCE_STAGE,

  // Planning life / disability / annuity — split off Life & Health onto
  // its own board 2026-08-29 (Grant: "let Ryan do what he wants. Split
  // life and health."). Same five stages, same slug, same trigger point
  // as its sibling above — the split changed which board a record lives
  // on, not when its product becomes real.
  "planning:planning_life_case": PLANNING_POLICY_IN_FORCE_STAGE,

  // Planning Wills & Trusts — no carrier. The estate plan exists once it
  // is signed, so the first stage that is true after signing.
  "planning:*": "post_signing_follow_up",
  // An employer's group long-term-care implementation file delivers a GROUP
  // PLAN, which is its own record (employer groups, design step 3), not a
  // person's case. Null on purpose: no stage here creates a product record.
  "planning:group_ltc_implementation": null,
  "planning:estate_plan": "post_signing_follow_up",
  "planning:estate_plan_case": "post_signing_follow_up",
};

// ─── The stage that creates an employer's GROUP PLAN ───
//
// An employer's implementation file does not deliver a household's product
// record (its board is null in the table above); it delivers the group plan,
// the contract with the business (shared/db/group-plans.ts, design step 3).
// Same shape and same reasoning as the product table: plain slug equality,
// so a renamed or deleted column degrades to "created on completion".
// Every value is a real slug on that board, asserted by
// tests/unit/product-materialization.test.ts.
const GROUP_PLAN_STAGE_BY_BOARD: Record<string, string> = {
  "planning:group_ltc_implementation": "carrier_issued",
};

/** The line of coverage each employer implementation board delivers. */
export const GROUP_PLAN_LINE_BY_TRANSACTION_TYPE: Record<string, string> = {
  group_ltc_implementation: "long_term_care",
};

/** The stage at which this employer board's group plan is created, or null
 *  when the board delivers no group plan. */
export function groupPlanStageFor(
  companySlug: string | null | undefined,
  transactionType: string | null | undefined,
): string | null {
  const key = `${(companySlug ?? "").trim()}:${(transactionType ?? "").trim()}`;
  return GROUP_PLAN_STAGE_BY_BOARD[key] ?? null;
}

/** Does entering `stage` on this board mean the group plan now exists? */
export function stageCreatesGroupPlan(args: {
  companySlug: string | null | undefined;
  transactionType: string | null | undefined;
  stage: string;
}): boolean {
  const trigger = groupPlanStageFor(args.companySlug, args.transactionType);
  return trigger !== null && trigger === args.stage;
}

// Realty is excluded from this module entirely, at every entry point.
//
// A realty product record IS a specific property — address, MLS number,
// listing vs. purchase side — which cannot be invented from a company and
// a transaction_type. Realty therefore creates its property record UP
// FRONT (transaction-create.ts writes the realty_listings /
// realty_purchases row with a validated address), and its unlinked
// backlog has a purpose-built "Link property record" picker
// (realty-transaction-links.ts). Both predate this change and both stay
// exactly as they are. Same carve-out, same reason, as the completion
// path's (tests/api/realty-transaction.test.ts).
export const REALTY_SLUG = "realty";

/**
 * The stage at which this board's product record should be created, or
 * null when the board has no such stage and completion is the trigger.
 *
 * Realty returns null — it is excluded (see REALTY_SLUG above).
 */
export function productStageFor(
  companySlug: string | null | undefined,
  transactionType: string | null | undefined,
): string | null {
  const company = (companySlug ?? "").trim();
  if (!company || company === REALTY_SLUG) return null;
  const exact = `${company}:${(transactionType ?? "").trim()}`;
  if (exact in PRODUCT_STAGE_BY_BOARD) return PRODUCT_STAGE_BY_BOARD[exact] ?? null;
  const byCompany = `${company}:*`;
  if (byCompany in PRODUCT_STAGE_BY_BOARD) return PRODUCT_STAGE_BY_BOARD[byCompany] ?? null;
  return null;
}

/**
 * Does entering `stage` on this board mean the product now exists?
 *
 * Deliberately a plain slug equality and NOT a check that the stage is
 * still on the board: users can rename, reorder and DELETE columns in the
 * pipeline editor without a deploy (Ryan owns Planning's board). If the
 * trigger column is gone, no record can ever enter it, so this simply
 * never fires and the completion path materializes instead — the
 * degradation is "later than ideal", never "not at all".
 */
export function stageCreatesProduct(args: {
  companySlug: string | null | undefined;
  transactionType: string | null | undefined;
  stage: string;
}): boolean {
  const trigger = productStageFor(args.companySlug, args.transactionType);
  return trigger !== null && trigger === args.stage;
}

/**
 * The human label of the stage that creates this board's product record,
 * e.g. "Policy Issued" — for UI copy that has to name it. Null when the
 * board has no such stage (the product is created on completion), or when
 * the trigger slug is no longer a column on the board.
 */
export function productStageLabel(
  companySlug: string | null | undefined,
  transactionType: string | null | undefined,
): string | null {
  const trigger = productStageFor(companySlug, transactionType);
  if (!trigger) return null;
  const board = lookupStageTemplate({
    companySlug: (companySlug ?? "").trim(),
    workflow: "transaction",
    type: (transactionType ?? "").trim(),
  });
  return board.stages.find((st) => st.slug === trigger)?.label ?? null;
}
